/**
 * HonorarStep — FSP-Honorartabelle (gültig ab 01.04.2025)
 * ---------------------------------------------------------
 * Workflow:
 *   1. Beim Öffnen des Steps wird Honorar AUTOMATISCH einmalig berechnet
 *      aus calculation.summary.subtotal_net + valuation.{wbw, wertminderung}
 *      + photos.length + inspection.distance_km.
 *   2. User kann Optional-Nebenkosten via Checkboxen ergänzen → Recompute.
 *   3. User kann manuelle Kalkulation aktivieren (+25% auf Grundhonorar).
 *   4. User kann Schreibseiten / 2.-Satz-Fotos editieren.
 *
 * Alle Tabellen-Werte sind brutto inkl. MwSt., die Anzeige zeigt
 * brutto/netto/MwSt getrennt.
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Receipt, Save, Loader2, Wand2, Info, Plus, Minus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtEur = (v) => (Number(v || 0)).toLocaleString("de-DE", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
});

// Mapping key → Human-Label (für Optional-Nebenkosten)
const OPTIONAL_LABELS = {
  fehlerspeicher_auslesen: "Fehlerspeicher auslesen (82,11 €)",
  achsvermessung: "Achsvermessung ohne Einstellung (177,31 €)",
  karosserievermessung: "Karosserievermessung zzgl. Demontage (355,81 €)",
  demontage_pro_stunde: "Demontagearbeiten zur Diagnose / Stunde (142,80 €)",
  hv_sicherung: "Sicherungsmaßnahme bei Hybrid-/E-Fzg (177,31 €)",
  hv_batterie_check: "HV-Batterie Zustandscheck (149,00 €)",
  hebebuehne_bereitstellung: "Hebebühnenbereitstellung (70,21 €)",
};

export default function HonorarStep({
  value,
  calculation,
  valuation,
  photoCount = 0,
  inspectionDistanceKm = null,
  caseId,
  onChange,
  onSave,
  saving = false,
  readOnly = false,
}) {
  const h = value || {};
  const [computing, setComputing] = useState(false);
  const autoLoadedRef = useRef(false);

  const autoGenerate = async () => {
    setComputing(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/honorar/auto-generate`);
      onChange(data.honorar);
      toast.success("Honorar aus Kalkulation generiert");
    } catch (e) {
      toast.error(apiError(e) || "Konnte Honorar nicht generieren");
    } finally {
      setComputing(false);
    }
  };

  // Beim ersten Öffnen des Steps: wenn noch kein Honorar berechnet wurde
  // ODER total_gross == 0 → Auto-Generate. Verhindert dass wir später
  // versehentlich user-edits überschreiben.
  useEffect(() => {
    if (autoLoadedRef.current) return;
    if (readOnly || !caseId) return;
    const hasHonorar = h.total_gross && Number(h.total_gross) > 0;
    if (hasHonorar) {
      autoLoadedRef.current = true;
      return;
    }
    // Nur generieren wenn calculation+valuation tatsächlich Werte enthalten
    const hasData = Number(calculation?.summary?.subtotal_net || 0) > 0
                 || Number(valuation?.replacement_value || 0) > 0;
    if (!hasData) {
      autoLoadedRef.current = true;
      return;
    }
    autoLoadedRef.current = true;
    // Defer to next tick to avoid setState during render effect synchronously.
    Promise.resolve().then(autoGenerate);
  }, [caseId]);

  // Recompute Honorar with new settings.
  function callRecompute(s) {
    setComputing(true);
    api.post(`/cases/${caseId}/honorar/recompute`, s)
      .then((res) => onChange(res.data.honorar))
      .catch((e) => toast.error(apiError(e) || "Neuberechnung fehlgeschlagen"))
      .finally(() => setComputing(false));
  }

  // Build settings payload immutably (avoid React-Compiler-Strict
  // mutation warnings). Pass `overrides` to change individual fields.
  const buildSettings = (overrides) => ({
    optional_keys: h.optional_keys || [],
    manuelle_kalkulation: !!h.manuelle_kalkulation,
    schreibseiten: Number(h.schreibseiten || 8),
    photo_zweitsatz_count: Number(h.photo_zweitsatz_count || 0),
    demontage_stunden: Number(h.demontage_stunden || 0),
    ...(overrides || {}),
  });

  const toggleOptional = (key) => {
    const current = h.optional_keys || [];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    callRecompute(buildSettings({ optional_keys: next }));
  };

  const hasComputedHonorar = Number(h.total_gross || 0) > 0;
  const hasInputs = Number(calculation?.summary?.subtotal_net || 0) > 0;

  // ── Render ──
  return (
    <div className="space-y-5" data-testid="honorar-step">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Receipt className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="font-display font-semibold text-zinc-900">Honorar</div>
            <div className="text-xs text-zinc-500">FSP-Vergütungstabelle gültig ab 01.04.2025</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && hasInputs && (
            <Button
              onClick={autoGenerate}
              disabled={computing}
              size="sm"
              variant="outline"
              className="h-9"
              data-testid="honorar-regenerate"
            >
              {computing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
              Aus Kalkulation neu berechnen
            </Button>
          )}
          {!readOnly && (
            <Button onClick={onSave} disabled={saving} size="sm" className="h-9" data-testid="honorar-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Speichern
            </Button>
          )}
        </div>
      </div>

      {/* Inputs-Hinweis falls keine Daten */}
      {!hasInputs && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3 items-start" data-testid="honorar-no-inputs">
          <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Noch keine Kalkulation vorhanden</div>
            <div className="text-amber-800 mt-1">
              Bitte zuerst die Kalkulation + Bewertung ausfüllen. Das Honorar wird dann automatisch aus
              Reparaturkosten netto + Wertminderung + Wiederbeschaffungswert berechnet.
            </div>
          </div>
        </div>
      )}

      {/* Computed Honorar Display */}
      {hasComputedHonorar && (
        <div className="space-y-4" data-testid="honorar-result">
          {/* Schadenhöhe Card */}
          <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2">
              Schadenhöhe (min aus Rep+Wertminderung / Wiederbeschaffungswert)
            </div>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-3xl font-bold text-emerald-900 font-mono tabular-nums" data-testid="honorar-schadenhoehe">
                  {fmtEur(h.schadenhoehe)}
                </div>
                <div className="text-xs text-emerald-700 mt-1">
                  Gewählte Tabellen-Stufe: bis {fmtEur(h.stufe)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-emerald-700">Grundhonorar (brutto)</div>
                <div className="text-2xl font-bold text-emerald-900 font-mono tabular-nums" data-testid="honorar-base-fee">
                  {fmtEur(h.base_fee)}
                </div>
              </div>
            </div>
          </div>

          {/* Manuelle Kalkulation Toggle */}
          {!readOnly && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white">
              <Checkbox
                id="manuelle-kalk"
                checked={!!h.manuelle_kalkulation}
                onCheckedChange={(checked) => {
                  callRecompute(buildSettings({ manuelle_kalkulation: !!checked }));
                }}
                disabled={computing}
                data-testid="honorar-manuelle-kalk"
              />
              <Label htmlFor="manuelle-kalk" className="text-sm cursor-pointer flex-1">
                Manuelle Kalkulation (+25 % Zuschlag auf Grundhonorar)
              </Label>
              {h.manuelle_kalk_surcharge > 0 && (
                <span className="text-sm font-mono font-semibold text-amber-700">
                  +{fmtEur(h.manuelle_kalk_surcharge)}
                </span>
              )}
            </div>
          )}

          {/* Standard-Nebenkosten Breakdown */}
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-600">
                Standard-Nebenkosten
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                Σ {fmtEur(h.nebenkosten_sum)}
              </div>
            </div>
            <ul className="divide-y divide-zinc-100">
              {(h.nebenkosten_items || []).map((n, i) => (
                <li
                  key={n.key + i}
                  className="px-4 py-2.5 flex items-center justify-between text-sm"
                  data-testid={`honorar-nk-${n.key}`}
                >
                  <span className="text-zinc-700">{n.label}</span>
                  <span className="font-mono tabular-nums font-semibold text-zinc-900">
                    {fmtEur(n.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Editable Quantities */}
          {!readOnly && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumberField
                label="Schreibseiten"
                value={h.schreibseiten ?? 8}
                onCommit={(n) => {
                  callRecompute(buildSettings({ schreibseiten: Math.max(0, n) }));
                }}
                disabled={computing}
                testid="honorar-schreibseiten"
              />
              <NumberField
                label="Lichtbilder 2. Satz"
                value={h.photo_zweitsatz_count ?? 0}
                onCommit={(n) => {
                  callRecompute(buildSettings({ photo_zweitsatz_count: Math.max(0, n) }));
                }}
                disabled={computing}
                testid="honorar-zweitsatz"
              />
              <NumberField
                label="Demontage-Stunden"
                value={h.demontage_stunden ?? 0}
                onCommit={(n) => {
                  callRecompute(buildSettings({ demontage_stunden: Math.max(0, n) }));
                }}
                disabled={computing || !(h.optional_keys || []).includes("demontage_pro_stunde")}
                step="0.5"
                testid="honorar-demontage"
              />
            </div>
          )}

          {/* Optional-Nebenkosten (Checkbox-Liste) */}
          {!readOnly && (
            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-600">
                  Optionale Nebenkosten (nur im Bedarfsfall)
                </div>
              </div>
              <ul className="divide-y divide-zinc-100">
                {Object.entries(OPTIONAL_LABELS).map(([key, label]) => {
                  const active = (h.optional_keys || []).includes(key);
                  const item = (h.optionale_nebenkosten_items || []).find((i) => i.key === key);
                  return (
                    <li
                      key={key}
                      className="px-4 py-2.5 flex items-center justify-between text-sm gap-3"
                      data-testid={`honorar-opt-${key}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Checkbox
                          id={`opt-${key}`}
                          checked={active}
                          onCheckedChange={() => toggleOptional(key)}
                          disabled={computing}
                          data-testid={`honorar-opt-${key}-cb`}
                        />
                        <Label htmlFor={`opt-${key}`} className="cursor-pointer text-zinc-700 truncate">
                          {label}
                        </Label>
                      </div>
                      {item && (
                        <span className="font-mono tabular-nums font-semibold text-emerald-700 shrink-0">
                          {fmtEur(item.amount)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {h.optional_sum > 0 && (
                <div className="px-4 py-2.5 bg-emerald-50 border-t border-emerald-200 flex justify-between text-sm font-semibold">
                  <span className="text-emerald-900">Summe Optional</span>
                  <span className="font-mono tabular-nums text-emerald-900">{fmtEur(h.optional_sum)}</span>
                </div>
              )}
            </div>
          )}

          {/* Total Summary */}
          <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-100 p-5">
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-emerald-800">Grundhonorar</span>
                <span className="font-mono tabular-nums text-emerald-900">{fmtEur(h.base_fee)}</span>
              </div>
              {h.manuelle_kalk_surcharge > 0 && (
                <div className="flex justify-between">
                  <span className="text-emerald-800">+ 25 % Manuelle Kalkulation</span>
                  <span className="font-mono tabular-nums text-emerald-900">{fmtEur(h.manuelle_kalk_surcharge)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-emerald-800">+ Nebenkosten</span>
                <span className="font-mono tabular-nums text-emerald-900">{fmtEur(h.nebenkosten_sum)}</span>
              </div>
              {h.optional_sum > 0 && (
                <div className="flex justify-between">
                  <span className="text-emerald-800">+ Optionale Nebenkosten</span>
                  <span className="font-mono tabular-nums text-emerald-900">{fmtEur(h.optional_sum)}</span>
                </div>
              )}
              <div className="border-t border-emerald-300 my-2" />
              <div className="flex justify-between text-xs text-emerald-700">
                <span>Netto</span>
                <span className="font-mono tabular-nums">{fmtEur(h.subtotal_net)}</span>
              </div>
              <div className="flex justify-between text-xs text-emerald-700">
                <span>MwSt {h.tax_rate ?? 19} %</span>
                <span className="font-mono tabular-nums">{fmtEur(h.tax_amount)}</span>
              </div>
              <div className="border-t border-emerald-400 my-2" />
              <div className="flex justify-between text-lg font-bold">
                <span className="text-emerald-900">Gesamthonorar brutto</span>
                <span className="font-mono tabular-nums text-emerald-900" data-testid="honorar-total-gross">
                  {fmtEur(h.total_gross)}
                </span>
              </div>
            </div>
          </div>

          {/* Footer-Hinweis */}
          <div className="text-[11px] text-zinc-500 text-center">
            Berechnungs-Basis: {photoCount} Lichtbild{photoCount === 1 ? "" : "er"} ·{" "}
            {inspectionDistanceKm ? `${inspectionDistanceKm} km Fahrt` : "Fahrt 15-km-Zone"} ·{" "}
            Tabelle {h.table_version || ""}
          </div>
        </div>
      )}

      {/* Generieren-Button wenn noch nichts berechnet, aber Inputs vorhanden */}
      {!hasComputedHonorar && hasInputs && (
        <div className="text-center py-8">
          <Button
            onClick={autoGenerate}
            disabled={computing || readOnly}
            size="lg"
            className="bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white"
            data-testid="honorar-generate-btn"
          >
            {computing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Honorar jetzt aus Kalkulation generieren
          </Button>
          <div className="text-xs text-zinc-500 mt-3">
            Berechnet Grundhonorar + Standard-Nebenkosten automatisch nach FSP-Tabelle
          </div>
        </div>
      )}
    </div>
  );
}

// Stepper-Number-Input mit Plus/Minus + Commit-on-Blur.
// Wir verwenden `key={value}` im Parent, sodass diese Komponente bei
// jedem Parent-Update vollständig neu gemountet wird — das vermeidet
// React-Compiler-Strict-Issues mit useEffect+setState.
function NumberField({ label, value, onCommit, disabled, step = "1", testid }) {
  const [draft, setDraft] = useState(String(value ?? 0));

  const commitNumber = (n) => {
    const safe = Math.max(0, n);
    setDraft(String(safe));
    if (safe !== Number(value)) onCommit(safe);
  };

  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
        {label}
      </Label>
      <div className="flex items-center gap-1 mt-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => commitNumber(Number(draft || 0) - Number(step))}
          disabled={disabled}
          className="h-9 w-9 p-0"
          data-testid={`${testid}-minus`}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <Input
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitNumber(Number(draft || 0))}
          step={step}
          disabled={disabled}
          className="h-9 text-center font-mono tabular-nums"
          data-testid={`${testid}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => commitNumber(Number(draft || 0) + Number(step))}
          disabled={disabled}
          className="h-9 w-9 p-0"
          data-testid={`${testid}-plus`}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
